import React from 'react';
import { Box, Chip, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import {
  Storage as StorageIcon,
  AcUnit as SnowflakeIcon,
  Cloud as CloudIcon,
  CloudQueue as CloudQueueIcon,
  BusinessCenter as BusinessCenterIcon,
  Groups as GroupsIcon,
  Payment as PaymentIcon,
  ShoppingCart as ShoppingCartIcon,
  BugReport as BugReportIcon,
  MenuBook as MenuBookIcon,
  GridView as GridViewIcon,
  SupportAgent as SupportAgentIcon,
  Chat as ChatIcon,
  Code as CodeIcon,
  Analytics as AnalyticsIcon,
  Campaign as CampaignIcon,
  Hub as HubIcon,
  Assignment as AssignmentIcon,
  Api as ApiIcon,
  FolderOpen as FolderIcon,
  TableChart as TableChartIcon,
  Inventory2 as BoxIcon,
} from '@mui/icons-material';
import { CatalogConnector, CONNECTOR_COLORS } from '../../types/connectorCatalog';

interface ConnectorCardProps {
  connector: CatalogConnector;
  isConnected?: boolean;
  connectionName?: string;
  isGlobal?: boolean;
  projectName?: string;
  onClick: (connector: CatalogConnector) => void;
}

/** Map catalog icon names to MUI icon components. */
const ICON_MAP: Record<string, React.ReactElement> = {
  StorageRounded: <StorageIcon />,
  AcUnitRounded: <SnowflakeIcon />,
  CloudRounded: <CloudIcon />,
  CloudQueueRounded: <CloudQueueIcon />,
  BusinessCenterRounded: <BusinessCenterIcon />,
  GroupsRounded: <GroupsIcon />,
  PaymentRounded: <PaymentIcon />,
  ShoppingCartRounded: <ShoppingCartIcon />,
  BugReportRounded: <BugReportIcon />,
  MenuBookRounded: <MenuBookIcon />,
  GridViewRounded: <GridViewIcon />,
  SupportAgentRounded: <SupportAgentIcon />,
  ChatRounded: <ChatIcon />,
  CodeRounded: <CodeIcon />,
  AnalyticsRounded: <AnalyticsIcon />,
  CampaignRounded: <CampaignIcon />,
  HubRounded: <HubIcon />,
  AssignmentRounded: <AssignmentIcon />,
  ApiRounded: <ApiIcon />,
  FolderRounded: <FolderIcon />,
  TableChartRounded: <TableChartIcon />,
  Inventory2Rounded: <BoxIcon />,
};

const ConnectorCard: React.FC<ConnectorCardProps> = ({
  connector,
  isConnected = false,
  connectionName,
  isGlobal,
  projectName,
  onClick,
}) => {
  const theme = useTheme();
  const brandColor = CONNECTOR_COLORS[connector.connector_id] || theme.palette.primary.main;
  const isInstalled = connector.installed !== false; // treat undefined as installed for backwards compat

  const icon = ICON_MAP[connector.icon] || <ApiIcon />;

  return (
    <Paper
      variant="outlined"
      onClick={() => onClick(connector)}
      sx={{
        p: 2.5,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        textAlign: 'center',
        height: '100%',
        minHeight: 180,
        transition: 'all 0.2s',
        borderColor: isConnected ? 'success.main' : 'divider',
        ...(!isInstalled && !isConnected && {
          opacity: 0.55,
        }),
        '&:hover': {
          borderColor: brandColor,
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 20px ${alpha(brandColor, 0.15)}`,
        },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(brandColor, 0.1),
          color: brandColor,
          '& .MuiSvgIcon-root': { fontSize: 28 },
        }}
      >
        {icon}
      </Box>

      {/* Name */}
      <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ width: '100%' }}>
        {connector.display_name}
      </Typography>

      {/* Description */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.4,
          minHeight: 32,
        }}
      >
        {connector.short_description}
      </Typography>

      {/* Direction chips + Status */}
      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" sx={{ mt: 'auto' }}>
        {isConnected ? (
          <Chip
            label={connectionName || 'Connected'}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        ) : (
          <>
            {(connector.direction === 'source' || connector.direction === 'both') && (
              <Chip label="Source" size="small" variant="outlined" color="primary" sx={{ fontSize: '0.7rem', height: 22 }} />
            )}
            {(connector.direction === 'destination' || connector.direction === 'both') && (
              <Chip label="Destination" size="small" variant="outlined" color="secondary" sx={{ fontSize: '0.7rem', height: 22 }} />
            )}
          </>
        )}
        {connector.is_popular && !isConnected && isInstalled && (
          <Chip label="Popular" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.dark' }} />
        )}
        {!isInstalled && !isConnected && (
          <Chip label="Coming Soon" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.dark' }} />
        )}
        {isConnected && isGlobal && (
          <Chip label="Shared" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20, color: 'text.secondary', borderColor: 'divider' }} />
        )}
        {isConnected && projectName && (
          <Chip label={projectName} size="small" variant="outlined" color="info" sx={{ fontSize: '0.65rem', height: 20, maxWidth: 100 }} />
        )}
      </Stack>
    </Paper>
  );
};

export default ConnectorCard;
